import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Database,
  Save,
  ArrowLeft,
  Loader2,
  FileText,
  Building2,
  Shield,
  AlertTriangle
} from 'lucide-react'
import { dataInputsService } from '@/services/dataInputs'
import { brandsService } from '@/services/brands'
import { ROUTES } from '@/utils/constants'
import { useAuth } from '@/context/AuthContext'
import toast from 'react-hot-toast'

const DataInputEdit = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // State for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState(null)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm()

  // Fetch brands for dropdown
  const {
    data: brands = [],
    isLoading: brandsLoading
  } = useQuery(
    'brands-for-select',
    () => brandsService.getForSelect(),
    {
      onError: (error) => {
        toast.error('Failed to load brands')
        console.error('Error loading brands:', error)
      }
    }
  )

  // Fetch data input data
  const {
    data: dataInput,
    isLoading: dataInputLoading,
    error: dataInputError
  } = useQuery(
    ['dataInput', id],
    () => dataInputsService.getById(id),
    {
      onError: (error) => {
        toast.error('Failed to load data input details')
        console.error('Error loading data input:', error)
      }
    }
  )

  // Populate form when data is available (from cache or server)
  useEffect(() => {
    if (dataInput) {
      setValue('name', dataInput.name)
      setValue('description', dataInput.description || '')
      setValue('brand_id', dataInput.brand_id)
      setValue('is_active', dataInput.is_active)
    }
  }, [dataInput, setValue])

  // Update data input mutation
  const updateDataInputMutation = useMutation(
    (dataInputData) => dataInputsService.update(id, dataInputData),
    {
      onSuccess: () => {
        toast.success('Data input updated successfully!')
        queryClient.invalidateQueries(['dataInput', id])
        queryClient.invalidateQueries('dataInputs')
        navigate(`/data-inputs/${id}`)
      },
      onError: (error) => {
        toast.error('Failed to update data input')
        console.error('Error updating data input:', error)
      }
    }
  )

  const onSubmit = async (data) => {
    // Convert brand_id to integer and store for confirmation
    const dataInputData = {
      ...data,
      brand_id: parseInt(data.brand_id)
    }
    setPendingFormData(dataInputData)
    setShowConfirmModal(true)
  }

  const confirmUpdate = () => {
    if (pendingFormData) {
      updateDataInputMutation.mutate(pendingFormData)
      setShowConfirmModal(false)
      setPendingFormData(null)
    }
  }

  const cancelUpdate = () => {
    setShowConfirmModal(false)
    setPendingFormData(null)
  }

  if (dataInputLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
          <span className="text-gray-500">Loading data input details...</span>
        </div>
      </div>
    )
  }

  if (dataInputError || !dataInput) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-red-100">
            <Database className="h-6 w-6 text-red-600" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Data input not found</h3>
          <p className="mt-1 text-sm text-gray-500">
            The data input you're trying to edit doesn't exist or has been deleted.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate(ROUTES.DATA_INPUTS)}
              className="btn-primary btn-sm"
            >
              Back to Data Inputs
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(`/data-inputs/${id}`)}
            className="btn-outline btn-sm flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Data Input
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Edit Data Input: {dataInput.name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              Update data input information and settings
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Data Input Information</h3>
            <p className="mt-1 text-sm text-gray-600">
              Update basic information about the data input
            </p>
          </div>
          <div className="p-6 space-y-6">
            {/* Data Input Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Data Input Name *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Database className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('name', {
                    required: 'Data input name is required',
                    minLength: {
                      value: 2,
                      message: 'Data input name must be at least 2 characters',
                    },
                    maxLength: {
                      value: 100,
                      message: 'Data input name must be less than 100 characters',
                    },
                  })}
                  type="text"
                  className={`input pl-10 ${
                    errors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter data input name"
                />
              </div>
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            {/* Brand Selection */}
            <div>
              <label htmlFor="brand_id" className="block text-sm font-medium text-gray-700">
                Brand *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Building2 className="h-5 w-5 text-gray-400" />
                </div>
                <select
                  {...register('brand_id', {
                    required: 'Brand selection is required',
                  })}
                  className={`input pl-10 ${
                    errors.brand_id ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  disabled={brandsLoading}
                >
                  <option value="">Select a brand</option>
                  {brands.map((brand) => (
                    <option key={brand.value} value={brand.value}>
                      {brand.label}
                    </option>
                  ))}
                </select>
              </div>
              {errors.brand_id && (
                <p className="mt-1 text-sm text-red-600">{errors.brand_id.message}</p>
              )}
              {brandsLoading && (
                <p className="mt-1 text-sm text-gray-500">Loading brands...</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <div className="mt-1 relative">
                <div className="absolute top-3 left-3 pointer-events-none">
                  <FileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  {...register('description', {
                    maxLength: {
                      value: 255,
                      message: 'Description must be less than 255 characters',
                    },
                  })}
                  rows={4}
                  className={`input pl-10 resize-none ${
                    errors.description ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : ''
                  }`}
                  placeholder="Enter data input description"
                />
              </div>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
              )}
            </div>

            {/* Active Status */}
            <div>
              <label className="flex items-center">
                <input
                  {...register('is_active')}
                  type="checkbox"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Active Data Input</span>
              </label>
              <p className="mt-1 text-xs text-gray-500">
                Inactive data inputs are hidden from users and cannot collect new data
              </p>
            </div>

            {/* User Isolation - Only show for admin and manager roles */}
            {(user?.role === 'admin' || user?.role === 'manager') && (
              <div>
                <label className="flex items-center">
                  <input
                    {...register('enable_user_isolation')}
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <div className="ml-2 flex items-center">
                    <Shield className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-700">Enable User Isolation</span>
                  </div>
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  When enabled, users with 'user' role can only see records they created themselves. Admin and manager roles can see all records regardless of this setting.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/data-inputs/${id}`)}
            className="btn-outline btn-md"
            disabled={updateDataInputMutation.isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary btn-md flex items-center"
            disabled={updateDataInputMutation.isLoading}
          >
            {updateDataInputMutation.isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Update Data Input
              </>
            )}
          </button>
        </div>
      </form>

      {/* Update Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={cancelUpdate}></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertTriangle className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Update Data Input
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to update <strong>{dataInput?.name}</strong>?
                        This will modify the data input settings and information.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  onClick={confirmUpdate}
                  disabled={updateDataInputMutation.isLoading}
                >
                  {updateDataInputMutation.isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Updating...
                    </>
                  ) : (
                    'Update Data Input'
                  )}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={cancelUpdate}
                  disabled={updateDataInputMutation.isLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default DataInputEdit
